/**
 * Cart Freebie Manager
 * Automatically adds/removes freebie products based on cart total milestones
 */

import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

class CartFreebieManager {
  constructor() {
    this.milestone1Amount = 0;
    this.milestone2Amount = 0;
    this.milestone3Amount = 0;
    this.freebieProduct2Id = null;
    this.freebieProduct3Id = null;
    this.freebieProduct2Handle = null;
    this.freebieProduct3Handle = null;
    this.freebieProduct2VariantId = null;
    this.freebieProduct3VariantId = null;
    this.isProcessing = false;
    this.cartSectionIds = [];
    this.isValidatingCheckout = false;

    this.init();
  }

  init() {
    // Load configuration
    this.loadConfig();

    // Get cart section IDs for updating
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        this.cartSectionIds.push(item.dataset.sectionId);
      }
    });

    // Listen for cart updates and quantity selector updates
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate.bind(this));
    document.addEventListener('cart:update', this.handleCartUpdate.bind(this));
    document.addEventListener('cartUpdate', this.handleCartUpdate.bind(this));
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityUpdate.bind(this));

    // Also listen for remove button clicks to disable checkout immediately
    document.addEventListener('click', this.handleRemoveButtonClick.bind(this), true);

    // Listen for checkout button clicks to validate freebies before checkout
    document.addEventListener('click', this.handleCheckoutButtonClick.bind(this), true);

    // Initial check after a delay to ensure cart is loaded
    setTimeout(() => {
      this.checkAndManageFreebies();
    }, 1000);
  }

  /**
   * Helper to reliably get the cart form element
   */
  getCartForm() {
    return document.querySelector('form#cart, form#cart_form, form#cart-form, form[action="/cart"]');
  }

  /**
   * Loads configuration from JSON script tag or data attributes.
   */
  loadConfig() {
    const configScript = document.getElementById('freebie-config');
    if (configScript) {
      try {
        const config = JSON.parse(configScript.textContent);
        this.milestone1Amount = parseFloat(config.milestone1Amount || 0);
        this.milestone2Amount = parseFloat(config.milestone2Amount || 0);
        this.milestone3Amount = parseFloat(config.milestone3Amount || 0);
        this.freebieProduct2Id = config.freebieProduct2Id || null;
        this.freebieProduct3Id = config.freebieProduct3Id || null;
        this.freebieProduct2Handle = (config.freebieProduct2Handle && config.freebieProduct2Handle !== 'null') ? config.freebieProduct2Handle : null;
        this.freebieProduct3Handle = (config.freebieProduct3Handle && config.freebieProduct3Handle !== 'null') ? config.freebieProduct3Handle : null;
        this.freebieProduct2VariantId = config.freebieProduct2VariantId || null;
        this.freebieProduct3VariantId = config.freebieProduct3VariantId || null;
        return true;
      } catch (e) {
        console.error('[Freebie Manager] Error parsing freebie config:', e, configScript.textContent);
        return false;
      }
    }
    const progressBar = document.querySelector('.cart-progress-bar');
    if (progressBar instanceof HTMLElement) {
      this.milestone1Amount = parseFloat(progressBar.dataset.milestone1Amount || '0');
      this.milestone2Amount = parseFloat(progressBar.dataset.milestone2Amount || '0');
      this.milestone3Amount = parseFloat(progressBar.dataset.milestone3Amount || '0');
      this.freebieProduct2Id = progressBar.dataset.freebieProduct2Id || null;
      this.freebieProduct3Id = progressBar.dataset.freebieProduct3Id || null;
      this.freebieProduct2VariantId = progressBar.dataset.freebieProduct2VariantId || null;
      this.freebieProduct3VariantId = progressBar.dataset.freebieProduct3VariantId || null;
      return true;
    }
    console.warn('[Freebie Manager] Config script tag and progress bar not found');
    return false;
  }

  handleRemoveButtonClick = (event) => {
    const target = event.target;
    const removeButton = target.closest('.cart-items__remove, button[aria-label*="remove"], button[aria-label*="Remove"]');
    if (removeButton) {
      this.disableCheckoutButton();
    }
  }

  handleCheckoutButtonClick = async (event) => {
    const target = event.target;
    const checkoutButton = target.closest('#checkout, [data-checkout-button="true"], .cart__checkout-button, .sr-headless-checkout');
    if (!checkoutButton) return;

    if (
      typeof window.__cartSecretOfferOnlyBlocksCheckout === 'function' &&
      window.__cartSecretOfferOnlyBlocksCheckout()
    ) {
      if (checkoutButton instanceof HTMLButtonElement && checkoutButton.disabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (typeof window.__clearCartSecretOfferOnly === 'function') {
        window.__clearCartSecretOfferOnly();
      }
      return;
    }

    if (this.isValidatingCheckout || this.isProcessing) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    if (checkoutButton instanceof HTMLButtonElement && checkoutButton.disabled) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.isValidatingCheckout = true;
    this.disableCheckoutButton();

    try {
      const cart = await this.getCart();
      if (!cart || !cart.items) {
        this.enableCheckoutButton();
        this.isValidatingCheckout = false;
        this.allowCheckout();
        return;
      }

      const cartTotal = this.calculateCartTotalExcludingFreebies(cart);
      let needsCorrection = false;

      // Check milestone 2
      if (this.milestone2Amount > 0 && (this.freebieProduct2Id || this.freebieProduct2Handle)) {
        const variantId = this.freebieProduct2VariantId ||
          (this.freebieProduct2Handle ? await this.getProductVariantId(this.freebieProduct2Handle) : null);
        if (variantId) {
          const isInCart = this.isFreebieInCart(cart, variantId);
          const shouldHave = cartTotal >= this.milestone2Amount;

          if (shouldHave !== isInCart) {
            needsCorrection = true;
            if (shouldHave && !isInCart) {
              await this.addFreebieProduct(variantId);
            } else if (!shouldHave && isInCart) {
              const lineKey = this.getFreebieLineKey(cart, variantId);
              if (lineKey) {
                await this.removeFreebieProduct(lineKey);
              }
            }
          }
        }
      }

      // Check milestone 3
      if (this.milestone3Amount > 0 && (this.freebieProduct3Id || this.freebieProduct3Handle)) {
        const variantId = this.freebieProduct3VariantId ||
          (this.freebieProduct3Handle ? await this.getProductVariantId(this.freebieProduct3Handle) : null);
        if (variantId) {
          const isInCart = this.isFreebieInCart(cart, variantId);
          const shouldHave = cartTotal >= this.milestone3Amount;

          if (shouldHave !== isInCart) {
            needsCorrection = true;
            if (shouldHave && !isInCart) {
              await this.addFreebieProduct(variantId);
            } else if (!shouldHave && isInCart) {
              const lineKey = this.getFreebieLineKey(cart, variantId);
              if (lineKey) {
                await this.removeFreebieProduct(lineKey);
              }
            }
          }
        }
      }

      if (needsCorrection) {
        let attempts = 0;
        const maxAttempts = 20;
        while (this.isProcessing && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      this.enableCheckoutButton();
      this.isValidatingCheckout = false;
      this.allowCheckout();
    } catch (error) {
      console.error('[Freebie Manager] Error validating freebies on checkout:', error);
      this.enableCheckoutButton();
      this.isValidatingCheckout = false;
      this.allowCheckout();
    }
  }

  allowCheckout() {
    this.isProcessing = false;
    this.enableCheckoutButton();

    const cartForm = this.getCartForm();
    if (cartForm) {
      delete cartForm.dataset.freebieRemovalActive;
    }

    const cartDrawerComponent = document.querySelector('cart-drawer-component');
    if (cartDrawerComponent) {
      const dialog = cartDrawerComponent.querySelector('dialog');
      if (dialog && dialog.open) {
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        dialog.close();
        dialog.classList.remove('dialog-closing');
      }
    }

    this.isValidatingCheckout = false;

    const srButton = document.querySelector('.sr-headless-checkout');
    if (srButton && typeof window !== 'undefined' && window.shiprocketCheckoutEvents && typeof window.shiprocketCheckoutEvents.buyCart === 'function') {
      window.shiprocketCheckoutEvents.buyCart(new Event('click'));
      return;
    }

    // FIX: Safely route to checkout without throwing the requestSubmit 'NotOwnedError'
    if (cartForm instanceof HTMLFormElement) {
      // Manually add the checkout parameter so Shopify routes to Checkout instead of updating cart
      if (!cartForm.querySelector('input[name="checkout"]')) {
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'Checkout';
        cartForm.appendChild(checkoutInput);
      }

      try {
        // By calling requestSubmit() with NO arguments, it works regardless of the button's DOM location
        cartForm.requestSubmit();
      } catch (e) {
        console.warn('[Freebie Manager] requestSubmit failed, falling back to submit()');
        cartForm.submit();
      }
    } else {
      // Ultimate fallback if the form is somehow missing
      window.location.href = '/checkout';
    }
  }

  handleQuantityUpdate = (event) => {
    this.disableCheckoutButton();
  }

  handleCartUpdate = async (event) => {
    if (event.detail?.data?.source === 'cart-freebie-manager') {
      return;
    }

    this.disableCheckoutButton();

    let configLoaded = this.loadConfig();
    if (!configLoaded) {
      await new Promise(resolve => setTimeout(resolve, 150));
      configLoaded = this.loadConfig();
    }
    if (!configLoaded) {
      await new Promise(resolve => setTimeout(resolve, 350));
      this.loadConfig();
    }

    const cartFromEvent = event.detail?.resource && Array.isArray(event.detail.resource?.items)
      ? event.detail.resource
      : null;

    await this.checkAndManageFreebies(cartFromEvent);

    let attempts = 0;
    const maxAttempts = 30; 
    while (this.isProcessing && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 50));
      attempts++;
    }

    this.enableCheckoutButton();
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  async getProductVariantId(productHandle) {
    if (!productHandle || productHandle === 'null' || String(productHandle).trim() === '') {
      return null;
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        console.warn('[Freebie Manager] getProductVariantId failed response for handle:', productHandle, response.status);
        return null;
      }
      const product = await response.json();
      const variant = product.variants?.find(v => v.available) || product.variants?.[0];
      return variant?.id || null;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  isItemFreebie(item) {
    if (!item || !item.properties) return false;
    if (Array.isArray(item.properties)) {
      return item.properties.some(prop => prop && prop.first === '_freebie' && prop.last === 'true');
    }
    if (typeof item.properties === 'object' && item.properties !== null) {
      return item.properties._freebie === 'true';
    }
    return false;
  }

  isFreebieInCart(cart, variantId) {
    if (!variantId || !cart || !cart.items) return false;
    return cart.items.some(item => {
      if (item.variant_id === variantId) {
        return this.isItemFreebie(item);
      }
      return false;
    });
  }

  getFreebieLineKey(cart, variantId) {
    if (!variantId || !cart || !cart.items) return null;
    const freebieItem = cart.items.find(item => {
      if (item.variant_id === variantId) {
        return this.isItemFreebie(item);
      }
      return false;
    });
    return freebieItem?.key || null;
  }

  async addFreebieProduct(variantId) {
    if (!variantId || this.isProcessing) return false;
    this.isProcessing = true;

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('properties[_freebie]', 'true');

      if (this.cartSectionIds.length > 0) {
        formData.append('sections', this.cartSectionIds.join(','));
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Accept': 'text/html' },
        body: formData
      });

      const data = await response.json();
      if (data.status) {
        console.error('Error adding freebie:', data.message, data);
        this.isProcessing = false;
        return false;
      }

      if (data.sections && this.cartSectionIds.length > 0) {
        this.cartSectionIds.forEach(sectionId => {
          if (data.sections[sectionId]) {
            morphSection(sectionId, data.sections[sectionId]);
          }
        });
      }

      document.dispatchEvent(new CartUpdateEvent(data, 'cart-freebie-manager', {
        source: 'cart-freebie-manager',
        sections: data.sections
      }));

      this.isProcessing = false;
      return true;
    } catch (error) {
      console.error('Error adding freebie product:', error);
      this.isProcessing = false;
      return false;
    }
  }

  preventCheckoutClick = (event) => {
    const target = event.target;
    const checkoutButton = target.closest('#checkout, [data-checkout-button="true"], .cart__checkout-button, .sr-headless-checkout');
    if (!checkoutButton) return;

    if (this.isProcessing || this.isValidatingCheckout) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }

    const cartForm = this.getCartForm();
    if (cartForm && cartForm.dataset.freebieRemovalActive === 'true') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }

  preventCheckoutSubmit = (event) => {
    const cartForm = this.getCartForm();
    if (cartForm && cartForm.dataset.freebieRemovalActive === 'true') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  }

  disableCheckoutButton() {
    document.addEventListener('click', this.preventCheckoutClick, true);
    
    const cartForm = this.getCartForm();
    if (cartForm instanceof HTMLFormElement) {
      cartForm.dataset.freebieRemovalActive = 'true';
      cartForm.addEventListener('submit', this.preventCheckoutSubmit, true);
    }

    const tryDisableButton = () => {
      const checkoutButton = document.querySelector('.sr-headless-checkout') ||
        document.querySelector('[data-checkout-button="true"]') ||
        document.getElementById('checkout');
      if (checkoutButton instanceof HTMLElement) {
        checkoutButton.disabled = true;
        checkoutButton.setAttribute('disabled', '');
        checkoutButton.setAttribute('aria-busy', 'true');
        checkoutButton.classList.add('cart-freebie-manager--button-disabled');
        checkoutButton.addEventListener('click', this.preventCheckoutClick, true);
        return true;
      }
      return false;
    };

    if (!tryDisableButton()) {
      setTimeout(() => { tryDisableButton(); }, 50);
    }
  }

  enableCheckoutButton() {
    try {
      document.removeEventListener('click', this.preventCheckoutClick, true);
    } catch (e) {}

    const cartForm = this.getCartForm();
    if (cartForm instanceof HTMLFormElement) {
      try {
        cartForm.removeEventListener('submit', this.preventCheckoutSubmit, true);
      } catch (e) {}
      delete cartForm.dataset.freebieRemovalActive;
    }

    const checkoutButton = document.querySelector('.sr-headless-checkout') ||
      document.querySelector('[data-checkout-button="true"]') ||
      document.getElementById('checkout');
    if (checkoutButton instanceof HTMLElement) {
      checkoutButton.disabled = false;
      checkoutButton.removeAttribute('disabled');
      checkoutButton.removeAttribute('aria-busy');
      checkoutButton.classList.remove('cart-freebie-manager--button-disabled');
      try {
        checkoutButton.removeEventListener('click', this.preventCheckoutClick, true);
      } catch (e) {}
    }
  }

  async removeFreebieProduct(lineKey) {
    if (!lineKey || this.isProcessing) {
      return false;
    }
    this.isProcessing = true;

    try {
      const cart = await this.getCart();
      if (!cart || !cart.items) {
        this.isProcessing = false;
        return false;
      }

      const lineItem = cart.items.find(item => item.key === lineKey);
      if (!lineItem || !this.isItemFreebie(lineItem)) {
        this.isProcessing = false;
        return false;
      }

      const lineItemIndex = cart.items.findIndex(item => item.key === lineKey);
      const lineNumber = lineItemIndex + 1;

      const body = JSON.stringify({
        line: lineNumber,
        quantity: 0,
        sections: this.cartSectionIds.join(','),
        sections_url: window.location.pathname
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const responseText = await response.text();

      if (!response.ok) {
        this.isProcessing = false;
        return false;
      }

      const data = JSON.parse(responseText);
      if (data.errors || data.status) {
        this.isProcessing = false;
        return false;
      }

      if (data.sections && this.cartSectionIds.length > 0) {
        this.cartSectionIds.forEach(sectionId => {
          if (data.sections[sectionId]) {
            morphSection(sectionId, data.sections[sectionId]);
          }
        });
      }

      document.dispatchEvent(new CartUpdateEvent(data, 'cart-freebie-manager', {
        source: 'cart-freebie-manager',
        sections: data.sections
      }));

      this.isProcessing = false;
      return true;
    } catch (error) {
      console.error('Error removing freebie product:', error);
      this.isProcessing = false;
      this.enableCheckoutButton();
      return false;
    }
  }

  calculateCartTotalExcludingFreebies(cart) {
    if (!cart || !cart.items) return 0;
    let total = 0;
    cart.items.forEach(item => {
      const isFreebie = this.isItemFreebie(item);
      const linePrice = item.final_line_price || item.line_price || 0;
      if (isFreebie) return;
      total += linePrice;
    });
    return total / 100;
  }

  async checkAndManageFreebies(cartFromEvent = null) {
    if (this.isProcessing) return;

    const cart = cartFromEvent && Array.isArray(cartFromEvent.items)
      ? cartFromEvent
      : await this.getCart();
    if (!cart || !cart.items) return;

    const cartTotal = this.calculateCartTotalExcludingFreebies(cart);

    if (this.milestone2Amount > 0 && (this.freebieProduct2Id || this.freebieProduct2Handle)) {
      const variantId = this.freebieProduct2VariantId ||
        (this.freebieProduct2Handle ? await this.getProductVariantId(this.freebieProduct2Handle) : null);
      if (variantId) {
        const isInCart = this.isFreebieInCart(cart, variantId);
        if (cartTotal >= this.milestone2Amount && !isInCart) {
          await this.addFreebieProduct(variantId);
        } else if (cartTotal < this.milestone2Amount && isInCart) {
          const lineKey = this.getFreebieLineKey(cart, variantId);
          if (lineKey) await this.removeFreebieProduct(lineKey);
        }
      }
    }

    if (this.milestone3Amount > 0 && (this.freebieProduct3Id || this.freebieProduct3Handle)) {
      const variantId = this.freebieProduct3VariantId ||
        (this.freebieProduct3Handle ? await this.getProductVariantId(this.freebieProduct3Handle) : null);
      if (variantId) {
        const isInCart = this.isFreebieInCart(cart, variantId);
        if (cartTotal >= this.milestone3Amount && !isInCart) {
          await this.addFreebieProduct(variantId);
        } else if (cartTotal < this.milestone3Amount && isInCart) {
          const lineKey = this.getFreebieLineKey(cart, variantId);
          if (lineKey) await this.removeFreebieProduct(lineKey);
        }
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // window.cartFreebieManager = new CartFreebieManager();
  });
} else {
  // window.cartFreebieManager = new CartFreebieManager();
}